import * as Highcharts from "highcharts";
import { formatterCurrency } from "../../../../utils/formatacaoNumeros";
import { OperadoraFatia, PrevisaoMes } from "../../../../services/home";
import { FONTE } from "../estilos/tokens";
import { PALETA_CLARA, Paleta } from "../../../../styles/theme/paletaCores";
import { coresPorRanking } from "./coresOperadoras";

require("highcharts/modules/broken-axis")(Highcharts);

const VALOR_DESPREZIVEL = 1e-9;
const RAZAO_PARA_QUEBRAR = 3;
const FOLGA_ACIMA_DOS_MENORES = 1.15;
const SOBRA_VISIVEL_DO_CORTADO = 0.5;
const ESPACO_ROTULO = 0.3;
const LARGURA_ESTREITA = 560;
const ALTURA_POR_BARRA = 48;
const ALTURA_EIXOS = 40;
const ALTURA_MINIMA = 160;
const ESPESSURA_POR_FAIXA = 0.42;
const ESPESSURA_MINIMA = 16;
const ESPESSURA_MAXIMA = 40;

export interface QuebraEixo {
  from: number;
  to: number;
}

export const barrasDoMes = (mes: PrevisaoMes): OperadoraFatia[] =>
  mes.operadoras.filter((o) => Math.abs(o.y) >= VALOR_DESPREZIVEL).sort((a, b) => b.y - a.y);

// Corta o trecho vazio entre as barras que destoam e as demais, todas do mesmo lado do zero
export const quebraDoEixo = (valores: number[]): QuebraEixo | null => {
  const ordenados = valores.filter((v) => Math.abs(v) >= VALOR_DESPREZIVEL).sort((a, b) => Math.abs(b) - Math.abs(a));
  let posicao = -1;
  for (let i = 0; i < ordenados.length - 1 && Math.sign(ordenados[i]) === Math.sign(ordenados[0]); i++) {
    if (Math.abs(ordenados[i]) / Math.abs(ordenados[i + 1]) > RAZAO_PARA_QUEBRAR) {
      posicao = i;
      break;
    }
  }
  if (posicao < 0) {
    return null;
  }
  const inicio = Math.abs(ordenados[posicao + 1]) * FOLGA_ACIMA_DOS_MENORES;
  const fim = Math.abs(ordenados[posicao]) - inicio * SOBRA_VISIVEL_DO_CORTADO;
  return ordenados[0] > 0 ? { from: inicio, to: fim } : { from: -fim, to: -inicio };
};

export const alturaGraficoPrevisoes = (quantidadeBarras: number): number =>
  Math.max(ALTURA_MINIMA, quantidadeBarras * ALTURA_POR_BARRA + ALTURA_EIXOS);

const cortadaPelaQuebra = (valor: number, quebra: QuebraEixo | null): boolean =>
  !!quebra && (valor > 0 ? quebra.from > 0 && valor >= quebra.to : quebra.to < 0 && valor <= quebra.from);

const rotuloDaBarra = (valor: number, quebra: QuebraEixo | null, paleta: Paleta) =>
  cortadaPelaQuebra(valor, quebra)
    ? { inside: true, align: valor > 0 ? "right" : "left", x: valor > 0 ? -8 : 8, style: { color: paleta.textoSobreVariacao } }
    : { style: { color: valor > 0 ? paleta.verdeTexto : paleta.vermelhoTexto } };

const marcadorOperadora = (cor: string): string =>
  `<span style="display:inline-block;flex:none;width:10px;height:10px;border-radius:50%;background:${cor}"></span>`;

const rotuloOperadora = (nome: string, cor: string): string =>
  `<span style="display:inline-flex;align-items:center;gap:8px">
    ${marcadorOperadora(cor)}
    <span>${nome}</span>
  </span>`;

const rotuloOperadoraComValor = (nome: string, cor: string, valor: number, largura: number, paleta: Paleta): string =>
  `<span style="display:flex;align-items:center;gap:8px;width:${largura}px">
    ${marcadorOperadora(cor)}
    <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis">${nome}</span>
    <span style="flex:none;color:${valor > 0 ? paleta.verdeTexto : paleta.vermelhoTexto}">${formatterCurrency(valor)}</span>
  </span>`;

export const graficoEstreito = (largura: number): boolean => largura < LARGURA_ESTREITA;

export const montaOpcoesPrevisoes = (
  mes: PrevisaoMes,
  altura: number,
  estreito = false,
  paleta: Paleta = PALETA_CLARA
): Highcharts.Options => {
  const barras = barrasDoMes(mes);
  const corDe = coresPorRanking(barras, paleta);
  const temEntrada = barras.some((b) => b.y > 0);
  const temSaida = barras.some((b) => b.y < 0);
  const quebra = quebraDoEixo(barras.map((b) => b.y));
  const amplitude = Math.max(0, ...barras.map((b) => b.y)) - Math.min(0, ...barras.map((b) => b.y));
  // O padding do Highcharts e proporcional a faixa inteira, inclusive o trecho cortado pela quebra
  const fracaoVisivel = quebra && amplitude > 0 ? (amplitude - (quebra.to - quebra.from)) / amplitude : 1;
  const faixaPorBarra = (altura - ALTURA_EIXOS) / Math.max(1, barras.length);
  const espessura = Math.round(Math.min(ESPESSURA_MAXIMA, Math.max(ESPESSURA_MINIMA, faixaPorBarra * ESPESSURA_POR_FAIXA)));

  // Sem espaco lateral para nome e valor: no estreito os dois vao numa linha acima da barra
  const rotulosOperadoras = estreito
    ? {
        reserveSpace: false,
        x: 0,
        y: -(espessura / 2 + 6),
        formatter(this: any): string {
          return rotuloOperadoraComValor(this.value, corDe(this.value), barras[this.pos].y, this.chart.plotWidth, paleta);
        },
      }
    : {
        reserveSpace: true,
        formatter(this: any): string {
          return rotuloOperadora(this.value, corDe(this.value));
        },
      };

  return {
    credits: { enabled: false },
    chart: { type: "bar", height: altura, backgroundColor: "transparent", spacing: [8, 16, 8, estreito ? 16 : 0] },
    title: { text: undefined },
    legend: { enabled: false },
    tooltip: { enabled: false },
    xAxis: {
      categories: barras.map((b) => b.name),
      lineWidth: 0,
      tickLength: 0,
      labels: {
        useHTML: true,
        align: "left",
        ...rotulosOperadoras,
        style: { fontFamily: FONTE, fontSize: estreito ? "12px" : "13px", fontWeight: "600", color: paleta.navy },
      },
    },
    yAxis: {
      title: { text: undefined },
      gridLineWidth: 0,
      labels: { enabled: false },
      softMin: 0,
      softMax: 0,
      startOnTick: false,
      endOnTick: false,
      minPadding: temSaida && !estreito ? ESPACO_ROTULO * fracaoVisivel : 0,
      maxPadding: temEntrada && !estreito ? ESPACO_ROTULO * fracaoVisivel : 0,
      plotLines: [{ value: 0, width: 1, color: paleta.suave, zIndex: 3 }],
      ...(quebra ? { breaks: [quebra] } : {}),
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderWidth: 0,
        pointPadding: 0,
        groupPadding: 0,
        maxPointWidth: espessura,
        minPointLength: 3,
        states: { hover: { enabled: false }, inactive: { opacity: 1 } },
      },
    },
    series: [
      {
        type: "bar",
        name: mes.rotulo,
        dataLabels: {
          enabled: !estreito,
          inside: false,
          crop: false,
          overflow: "allow",
          formatter(this: any): string {
            return formatterCurrency(this.y);
          },
          style: { fontFamily: FONTE, fontSize: "13px", fontWeight: "600", textOutline: "none" },
        },
        data: barras.map((b) => ({
          y: b.y,
          color: b.y > 0 ? paleta.verdeVariacao : paleta.vermelhoVariacao,
          dataLabels: rotuloDaBarra(b.y, quebra, paleta),
        })),
      },
    ],
  };
};
